import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { CopyIcon, EllipsisVerticalIcon, SquarePenIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { useBuilderBoxApi } from '../../services/api/useBuilderBox.api';
import { BuilderBox } from '../../types/SimilarlySituated/BuilderBox.type';
import { analysesToOutcomes } from '../../utils/ssbBuilderBox';

const MAX_LISTED_CASE_TYPES = 3;

const BuildCardBox = styled(Box)(({ theme }) => ({
  border: '1px solid #d6d6d6',
  borderRadius: '8px',
  backgroundColor: '#fff',
  position: 'relative',
  display: 'flex',
  minHeight: '100px',
  padding: '20px 24px',
  transitionProperty: 'border-color, box-shadow',
  transition: '0.3s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.secondary.main,
    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
  },
}));

interface BuildCardProps {
  build: BuilderBox;
  activeSort: string;
  onOpen: () => void;
  onDuplicate: () => void;
  onRenamed: (build: BuilderBox) => void;
  onDeleted: (buildId: number) => void;
}

function BuildCard({ build, activeSort, onOpen, onDuplicate, onRenamed, onDeleted }: BuildCardProps) {
  const queryClient = useQueryClient();
  const { updateBuilderBox, deleteBuilderBox } = useBuilderBoxApi();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [newName, setNewName] = useState(build.name);

  const menuOpen = Boolean(anchorEl);
  const invalidateBuilds = () => queryClient.invalidateQueries({ queryKey: ['caseBuilderBoxes'], exact: false });

  const { mutate: renameBuild, isPending: isRenaming } = useMutation({
    mutationKey: ['renameBuilderBox', build.id],
    mutationFn: (name: string) => updateBuilderBox(build.id, { name }),
    onSuccess: (renamed) => {
      invalidateBuilds();
      onRenamed(renamed);
      setRenameOpen(false);
    },
  });

  const { mutate: removeBuild, isPending: isDeleting } = useMutation({
    mutationKey: ['deleteBuilderBox', build.id],
    mutationFn: () => deleteBuilderBox(build.id),
    onSuccess: () => {
      invalidateBuilds();
      onDeleted(build.id);
      setDeleteOpen(false);
    },
  });

  const handleDuplicateOptionClick = () => {
    onDuplicate();
    setAnchorEl(null);
  };

  const handleRenameOptionClick = () => {
    setNewName(build.name);
    setRenameOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteOptionClick = () => {
    setDeleteOpen(true);
    setAnchorEl(null);
  };

  const isSortedByCreation = activeSort === 'created_at:desc';
  const timestamp = formatDistanceToNow(new Date(isSortedByCreation ? build.created_at : build.updated_at), {
    addSuffix: true,
  });

  const caseTypeNames = build.condition_boxes
    .filter((conditionBox) => !conditionBox.is_exclude)
    .map((conditionBox) => conditionBox.name);
  const hiddenCaseTypes = caseTypeNames.length - MAX_LISTED_CASE_TYPES;
  const outcomeCount = analysesToOutcomes(build.analyses ?? []).length;

  return (
    <BuildCardBox>
      <Box
        role='button'
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen();
          }
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          flexGrow: 1,
          cursor: 'pointer',
        }}
      >
        <Typography
          title={build.name}
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#2d3748',
            paddingRight: '24px',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {build.name}
        </Typography>

        <Typography
          sx={{
            marginTop: '14px',
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            color: '#A0AEC0',
          }}
        >
          {caseTypeNames.length} {caseTypeNames.length === 1 ? 'case type' : 'case types'}
        </Typography>
        <Box component='ul' sx={{ margin: '6px 0 0', padding: 0, listStyle: 'none' }}>
          {caseTypeNames.slice(0, MAX_LISTED_CASE_TYPES).map((name, index) => (
            <Typography
              key={index}
              component='li'
              title={name}
              sx={{
                fontSize: '14px',
                lineHeight: '22px',
                paddingLeft: '14px',
                position: 'relative',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: '2px',
                  top: '9px',
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  backgroundColor: '#CBD5E0',
                },
              }}
            >
              {name}
            </Typography>
          ))}
          {hiddenCaseTypes > 0 && (
            <Typography
              component='li'
              sx={{
                fontSize: '13px',
                color: '#A0AEC0',
                marginTop: '4px',
                fontWeight: 500,
              }}
            >
              +&nbsp;{hiddenCaseTypes} more
            </Typography>
          )}
        </Box>

        <Divider sx={{ marginTop: 'auto', paddingTop: '14px', borderColor: '#F0F4F8' }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: '12px',
            paddingTop: '14px',
          }}
        >
          <Typography sx={{ fontSize: '12px', color: '#718096', whiteSpace: 'nowrap' }}>
            <Box component='span' sx={{ fontWeight: 600, color: '#2d3748' }}>
              {outcomeCount}
            </Box>{' '}
            {outcomeCount === 1 ? 'outcome selected' : 'outcomes selected'}
          </Typography>
          <Typography
            variant='caption'
            title={isSortedByCreation ? 'Created' : 'Last update'}
            sx={{ fontSize: '11px', color: '#a0aec0', whiteSpace: 'nowrap' }}
          >
            {timestamp}
          </Typography>
        </Box>
      </Box>

      <IconButton
        aria-label={`Options for ${build.name}`}
        aria-haspopup='true'
        aria-expanded={menuOpen ? 'true' : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        size='small'
        sx={{ position: 'absolute', top: '14px', right: '10px' }}
      >
        <EllipsisVerticalIcon size={17} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { style: { width: '20ch' } } }}
      >
        <MenuItem onClick={handleDuplicateOptionClick}>
          <CopyIcon style={{ marginRight: '16px', width: '20px' }} />
          Duplicate
        </MenuItem>
        <MenuItem onClick={handleRenameOptionClick}>
          <SquarePenIcon style={{ marginRight: '16px', width: '20px' }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleDeleteOptionClick} sx={{ color: 'error.main' }}>
          <Trash2Icon style={{ marginRight: '16px', width: '20px' }} />
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        fullWidth
        maxWidth='xs'
        slotProps={{
          paper: {
            component: 'form',
            onSubmit: (event: React.FormEvent) => {
              event.preventDefault();
              if (newName.trim() && newName.trim() !== build.name) {
                renameBuild(newName.trim());
              }
            },
          },
        }}
      >
        <DialogTitle>Rename</DialogTitle>
        <DialogContent>
          <TextField
            margin='dense'
            label='Box name'
            type='text'
            fullWidth
            variant='standard'
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} disabled={isRenaming} sx={{ color: '#000' }}>
            Cancel
          </Button>
          <Button
            type='submit'
            disabled={isRenaming || !newName.trim() || newName.trim() === build.name}
            disableElevation
            variant='contained'
          >
            {isRenaming ? 'Please wait...' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle>Delete box</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px' }}>
            Box <b>{build.name}</b> and all of its conditions will be deleted. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={isDeleting} sx={{ color: '#000' }}>
            Cancel
          </Button>
          <Button
            color='error'
            disabled={isDeleting}
            onClick={() => removeBuild()}
            disableElevation
            variant='contained'
          >
            {isDeleting ? 'Please wait...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </BuildCardBox>
  );
}

export default BuildCard;
